import { memo, useMemo } from "react"
import dayjs from "dayjs"
import { useLiveQueryImmediate } from "@/db/hooks"
import { cn } from "@/shared/utils/general/cn"
import { db, ReminderKind } from "@/db/schema"
import { humanReadableRule } from "@totodo/operations"
import type { DbObject } from "@/db/schema"
import { useFocusStore } from "@/features/focus/focusStore"
import { useViewContext } from "@/app/stores/viewContext"
import EditIcon from "@/shared/icons/totodoicons/stroke/edit.svg?react"
import ImageIcon from "@/shared/icons/pikaicons/stroke/media/photo-image-default.svg?react"
import ListIcon from "@/shared/icons/pikaicons/stroke/general/list-check-box.svg?react"
import BellSvg from "@/shared/icons/pikaicons/stroke/alerts/notification-bell-on.svg?react"
import AlarmSvg from "@/shared/icons/pikaicons/stroke/time/alarm-default.svg?react"
import RepeatSvg from "@/shared/icons/pikaicons/stroke/arrows-chevrons/repeat-square.svg?react"
import FocusTargetSvg from "@/shared/icons/pikaicons/stroke/general/focus-target.svg?react"
import SparkleAiSvg from "@/shared/icons/pikaicons/stroke/general/sparkle-ai-01.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskIconsProps {
  task: DbObject
  hasChecklist?: boolean
  /** Hide the focus indicator (e.g. when rendered inside the FocusBanner) */
  hideFocusIcon?: boolean
  /** Override the viewId (to avoid reading from global context) */
  viewId?: string
  className?: string
}

// ---------------------------------------------------------------------------
// Icon wrappers — v1 parity: pikaicons 18×18
// ---------------------------------------------------------------------------

/** Pencil icon — indicates task has notes */
function NoteIcon() {
  return <EditIcon width={18} height={18} />
}

/** Photo icon — indicates task note contains images */
function PhotoIcon() {
  return <ImageIcon width={18} height={18} />
}

/** Checklist icon — indicates task has checklist items */
function ChecklistIcon() {
  return <ListIcon width={18} height={18} />
}

/** Bell icon — indicates task has an active reminder */
function BellIcon() {
  return <BellSvg width={18} height={18} />
}

/** Alarm icon — indicates task has an active alarm */
function AlarmIcon() {
  return <AlarmSvg width={18} height={18} />
}

/** Repeat icon — indicates task is recurring (template or instance) */
function RepeatIcon() {
  return <RepeatSvg width={18} height={18} />
}

/** Focus target icon — indicates task is "In Focus" */
function FocusIcon() {
  return <FocusTargetSvg width={18} height={18} className="text-destructive/70" />
}

/** Sparkle icon — indicates AI enrichment state */
function AiSparkleIcon({ state }: { state: "pending" | "done" | "failed" }) {
  return (
    <SparkleAiSvg
      width={18}
      height={18}
      className={state === "pending" ? "animate-pulse text-primary/60" : state === "done" ? "text-primary/50" : "text-muted-foreground/40"}
    />
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Small indicator icons for task rows.
 *
 * v1 pattern: CardPreviewIcons.
 * Shows reminder icon (bell/alarm), note icon, and/or checklist icon.
 * All at 40% opacity, 14px.
 */
export const TaskIcons = memo(function TaskIcons({
  task,
  hasChecklist,
  hideFocusIcon,
  viewId: viewIdProp,
  className,
}: TaskIconsProps) {
  const hasImage = useMemo(
    () => Boolean(task.docHtml && task.docHtml.includes("<img")),
    [task.docHtml],
  )
  const hasText = useMemo(() => {
    // Check old note field OR docHtml text content (strip HTML tags)
    if (task.note && task.note.trim().length > 0) return true
    if (!task.docHtml) return false
    const text = task.docHtml.replace(/<[^>]*>/g, "").trim()
    return text.length > 0
  }, [task.note, task.docHtml])

  // Query active reminders (v1 CardPreviewIcons pattern)
  const [activeReminderKind] = useLiveQueryImmediate(
    async () => {
      const taskId = task.id
      if (!taskId) return null
      const now = dayjs().valueOf().toString()
      const reminders = await db.reminder
        .where("taskId")
        .equals(taskId)
        .filter(
          (r) =>
            (r.isDeleted === 0 || r.isDeleted === undefined) &&
            !!r.fireAt &&
            r.fireAt > now,
        )
        .toArray()
      if (!reminders.length) return null
      return reminders.some((r) => r.kind === ReminderKind.Alarm)
        ? ReminderKind.Alarm
        : ReminderKind.Reminder
    },
    ["task-reminder", task.id],
    null,
  )

  // For recurring templates: check if recurrence has reminderOffsetMs set
  // and fetch the frequency summary for inline display
  const [recurrenceData] = useLiveQueryImmediate(
    async () => {
      if (!task.isRecurringTemplate || !task.id) return null
      const rec = await db.recurrence.get(task.id)
      if (!rec) return null
      const reminderKind = rec.reminderOffsetMs != null
        ? (rec.reminderKind === "alarm" ? ReminderKind.Alarm : ReminderKind.Reminder)
        : null
      const summary = humanReadableRule(rec.rrule, rec.mode, rec.afterCompletionDays)
      // Shorten for compact display in task list
      const short = summary
        .replace(/every day/i, "daily")
        .replace(/every week/i, "weekly")
        .replace(/every month/i, "monthly")
        .replace(/every year/i, "yearly")
        .replace(/every (\d+) days?/i, "$1d")
        .replace(/every (\d+) weeks?/i, "$1w")
        .replace(/every (\d+) months?/i, "$1mo")
        .replace(/(\d+) days? after completion/i, "$1d after")
      return { reminderKind, summary: short, aiEnabled: rec.aiEnabled === 1 }
    },
    ["task-recurrence-data", task.id, task.isRecurringTemplate],
    null,
  )

  const effectiveReminderKind = activeReminderKind ?? recurrenceData?.reminderKind ?? null
  const hasActiveReminder = Boolean(effectiveReminderKind)
  const isRecurring = !!(task.isRecurringTemplate || task.recurringTemplateId)
  const focusStoreMatch = useFocusStore((s) => s.focusedIds.has(task.id!))
  const isFocused = !hideFocusIcon && focusStoreMatch
  const contextViewId = useViewContext((s) => s.viewId)
  const viewId = viewIdProp ?? contextViewId
  const hideRecurrence = viewId === "recurring"
  const aiState = task.aiState as "pending" | "done" | "failed" | undefined
  // Show sparkle for templates with AI enabled OR instances with aiState
  const showAiIcon = aiState || recurrenceData?.aiEnabled

  if (!hasText && !hasImage && !hasChecklist && !hasActiveReminder && (!isRecurring || hideRecurrence) && !isFocused && !showAiIcon)
    return null

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 pl-1 text-foreground/40",
        className,
      )}
    >
      {isFocused && <FocusIcon />}
      {isRecurring && !hideRecurrence && (
        <>
          <RepeatIcon />
          {recurrenceData?.summary && (
            <span className="text-[10px] leading-none">{recurrenceData.summary}</span>
          )}
        </>
      )}
      {hasActiveReminder &&
        (effectiveReminderKind === ReminderKind.Alarm ? (
          <AlarmIcon />
        ) : (
          <BellIcon />
        ))}
      {hasText && <NoteIcon />}
      {hasImage && <PhotoIcon />}
      {hasChecklist && <ChecklistIcon />}
      {showAiIcon && <AiSparkleIcon state={aiState ?? "done"} />}
    </span>
  )
})
